import {
  createContext,
  type FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { useTenantConfig } from '@/contexts/TenantConfig';
import { useCognitoAuth } from '@/contexts/Cognito';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useSessionStorage } from '@mantine/hooks';
import { IdentityProvider } from '@/lib/graphql/types';
import useDatadogRum from '@/hooks/useDatadogRum';

export interface AuthenticationStateParams {
  returnToUrl?: string;
}

export interface SessionEndOptions {
  // Don't perform after log out actions, remain in place.
  stayInPlace?: boolean;
  // Override the default redirect after log out location.
  redirectTo?: string;
}

export interface CognitoActionProps extends PropsWithChildren {
  // Log out
  endSession: (options?: SessionEndOptions) => Promise<void>;
  // Reload the user token from Cognito
  refetchUserMetadata: () => Promise<void>;
  // Maintain active access token
  possiblyRefreshAccessToken: () => Promise<void>;
  // Capture applications state and invoke the login flow
  redirectToLogin: (params?: AuthenticationStateParams) => void;
  // After a regular UI-driven login, perform actions to redirect the user to the correct page.
  redirectAfterLogin: () => void;
}

const stub = (): never => {
  throw new Error('You must wrap your component in <CognitoActionProvider>.');
};

export const CognitoActionContext = createContext<CognitoActionProps>({
  endSession: async (options?: SessionEndOptions) => stub(),
  refetchUserMetadata: async () => stub(),
  possiblyRefreshAccessToken: async () => stub(),
  redirectToLogin: () => stub(),
  redirectAfterLogin: () => stub(),
});

export const configureAmplify = ({
  userPoolId,
  userPoolClientId,
  idps,
}: {
  userPoolId: string;
  userPoolClientId: string;
  idps: IdentityProvider[];
}) => {
  // generate the user pool domain from the user pool ID. This is the pattern
  // we use when generating new pools in /services/tenants/src/aws/cognito/service.py
  const [region, poolCode] = userPoolId.split('_');
  const domain = `caregentic-${poolCode.toLowerCase()}.auth.${region}.amazoncognito.com`;

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          email: true,
          phone: true,
          // OAuth powers TPA login in the patient portal. May consider refactoring how this
          // works -- or depending on an extension to CognitoConfig in TenantConfig to prevent
          // redundant set-up in other portals.
          oauth: {
            domain,
            scopes: [
              'email',
              'openid',
              'phone',
              'profile',
              'aws.cognito.signin.user.admin',
            ],
            redirectSignIn: [`https://${window.location.host}/auth/tpa`],
            redirectSignOut: ['/'],
            responseType: 'code',
            providers: idps.map((idp) => ({ custom: idp.name })),
          },
        },
        signUpVerificationMethod: 'code',
        userAttributes: {
          email: { required: false },
        },
      },
    },
  });
};

export interface CognitoActionProviderProps extends PropsWithChildren {
  // The path in the application where users will be presented with the login screen.
  loginUrl: string;
  // The default path in the application where users will be redirected after a successful login.
  afterLoginUrl: string;
  // Where to return a user to after logging out. (defaults to loginUrl)
  afterLogOutUrl?: string;
}

/**
 * Cognito Action Provider enables creating and managing Cognito user sessions stored in the CognitoAuthContext,
 * via interactions with the AWS Amplify Auth library. These actions depend on Tenant configuration and GraphQL
 * API endpoints, so are separated from the underlying token and user information store.
 */
export const CognitoActionProvider: FunctionComponent<
  CognitoActionProviderProps
> = ({ children, loginUrl, afterLoginUrl, afterLogOutUrl = loginUrl }) => {
  const {
    setToken,
    setRefreshing,
    setIdpName,
    clearAuthState,
    setError: setAuthError,
  } = useCognitoAuth();
  const { replace, push } = useRouter();
  const { cognito } = useTenantConfig();
  const { addAction, addError } = useDatadogRum();

  const [amplifyConfigured, setAmplifyConfigured] = useState(false);

  const [appRestoreState, setAppRestoreState, clearAppRestoreState] =
    useSessionStorage<AuthenticationStateParams>({
      key: 'cognito-appRestoreState',
      defaultValue: {},
    });

  /**
   * Calls the underlying Amplify `fetchUserSession` to retrieve the current user's
   * session data, and sets it to the underlying CognitoAuthContext.
   */
  const fetchUserMetadata = useCallback(
    async ({ forceRefresh = false } = {}) => {
      setRefreshing(true);

      try {
        const session = await fetchAuthSession({ forceRefresh });
        if (session.tokens?.idToken) {
          const idToken = session.tokens.idToken.toString();
          setToken(idToken);
          setAuthError(null);
        }
      } catch (err) {
        setAuthError(
          new Error('Failed to fetch session data. Please log in again.')
        );
      } finally {
        setRefreshing(false);
      }
    },
    [setAuthError, setRefreshing, setToken]
  );

  /**
   * When the Tenant configuration arrives, configure Amplify.
   */
  useEffect(() => {
    if (cognito && !amplifyConfigured) {
      configureAmplify({
        userPoolId: cognito.userPoolId,
        userPoolClientId: cognito.clientId,
        idps: cognito.identityProviders ?? [],
      });
      setAmplifyConfigured(true);
    }
  }, [cognito, amplifyConfigured]);

  /**
   * After the Amplify SDK is configured, fetch the user's session data
   */
  useEffect(() => {
    if (amplifyConfigured) {
      fetchUserMetadata();
    }
  }, [amplifyConfigured, fetchUserMetadata]);

  /**
   * Redirect the user after login is successful.
   */
  const redirectAfterLogin = useCallback(() => {
    const redirectUrl = appRestoreState.returnToUrl ?? afterLoginUrl;
    clearAppRestoreState();
    replace(redirectUrl);
  }, [
    afterLoginUrl,
    appRestoreState.returnToUrl,
    clearAppRestoreState,
    replace,
  ]);

  /** Refresh the auth token, even if it has not expired */
  const refetchUserMetadata = useCallback(
    async () => fetchUserMetadata({ forceRefresh: true }),
    [fetchUserMetadata]
  );

  /** Trigger Cognito to update the user's auth token, if necessary */
  const possiblyRefreshAccessToken = useCallback(
    async () => fetchUserMetadata({ forceRefresh: false }),
    [fetchUserMetadata]
  );

  /**
   * Redirects the user to the login page, optionally setting setting in session storage the URL to return to after login.
   */
  const redirectToLogin = useCallback(
    (state: AuthenticationStateParams = {}) => {
      setAppRestoreState(state ?? {});
      replace(loginUrl);
    },
    [loginUrl, replace, setAppRestoreState]
  );

  /**
   * Logs the user out of Cognito, and redirects them to the default page according to the context configuration.
   */
  const endSession = useCallback(
    async ({ stayInPlace, redirectTo }: SessionEndOptions = {}) => {
      await signOut();

      if (!stayInPlace) {
        push(redirectTo ?? afterLogOutUrl);
      }
    },
    [push, afterLogOutUrl]
  );

  /**
   * Listen to Amplify SDK auth events to update the user's session data.
   */
  useEffect(() => {
    const cancelHubListener = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          await fetchUserMetadata();
          // log successful sign in
          addAction('UserSessionSignedIn');
          break;
        case 'signedOut':
          clearAuthState();
          // Clearing App Restore state after a timeout ensures that any redirect state
          //       *created* by components during log out gets cleared away.
          // TODO: CRE-3246 - Investigate a more robust way to handle this
          setTimeout(clearAppRestoreState, 1);
          addAction('UserSessionSignedOut');
          break;
        case 'tokenRefresh':
          fetchUserMetadata();
          break;
        case 'tokenRefresh_failure':
          // If token refresh fails (e.g. due to expired refresh token), log the user out to clean
          // up, which will redirect them to the login page.
          setAuthError(new Error('Your session expired. Please log in again.'));
          endSession();
          break;
        case 'signInWithRedirect':
          // Sign in after SAML or OAuth redirect from hosted login.
          await fetchUserMetadata();
          addAction('UserSessionSignedInWithIdP');
          break;
        case 'customOAuthState':
          // We presently only use customState to maintain IdP names. If that changes,
          // move to a model where we encode/decode a JSON blob for this field.
          setIdpName(payload.data);
          addAction('UserSessionIdPUse', { idp: payload.data });
          break;
        case 'signInWithRedirect_failure':
          // Sign in failed after SAML or OAuth redirect from hosted login.
          addError(new Error('Cognito signInWithRedirect Failure'));
          setAuthError(
            new Error(
              'A sign in error occurred while authenticating with the third party provider.'
            )
          );
          break;
      }
    });
    return () => cancelHubListener();
  }, [
    clearAuthState,
    fetchUserMetadata,
    clearAppRestoreState,
    setAuthError,
    endSession,
    setIdpName,
    addAction,
    addError,
  ]);

  return (
    <CognitoActionContext.Provider
      value={{
        endSession,
        redirectToLogin,
        redirectAfterLogin,
        refetchUserMetadata,
        possiblyRefreshAccessToken,
      }}>
      {children}
    </CognitoActionContext.Provider>
  );
};

export const useCognitoAction = (): CognitoActionProps => {
  const context = useContext(CognitoActionContext);
  return context;
};
